import React, { useState, useRef } from "react";
import Modal from "react-modal";
import axios from "axios";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { format } from "date-fns";
import "./App.css";
import "./Transitions.css";

Modal.setAppElement("#root");

const customModalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "320px",
    padding: "30px",
    textAlign: "center",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    border: "none",
  },
};

const PatientList = ({
  filter,
  fetchPatients,
  openModal,
  patients = [],
  selectedDoctor,
  allPatients = [],
  onLoadMore,
}) => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [note, setNote] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [actionModalIsOpen, setActionModalIsOpen] = useState(false);
  const [enceradoConfirmPatient, setEnceradoConfirmPatient] = useState(null);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const detailFileInputRef = useRef(null);

  const handleStatusChange = async (id, status, callNotes = "") => {
    try {
      await axios.put(`https://backendlab.vercel.app/patients/${id}`, {
        status,
        callNotes,
        updatedAt: new Date(),
      });
      fetchPatients();
    } catch (error) {
      console.error("Error updating patient status:", error);
    }
  };

  const handleRevertToNeedReady = async (id) => {
    try {
      await axios.put(`https://backendlab.vercel.app/patients/${id}/revertToNeedReady`);
      fetchPatients();
    } catch (error) {
      console.error("Error reverting patient to needReady:", error);
    }
  };

  const handleEnceradoComplete = async (patient) => {
    try {
      await axios.put(`https://backendlab.vercel.app/patients/${patient._id}`, {
        ...patient,
        enceradoCompleted: true,
        updatedAt: new Date(),
      });
      fetchPatients();
    } catch (error) {
      console.error("Error marking encerado as completed:", error);
    }
  };

  const handleNoteSubmit = async () => {
    if (selectedPatient) {
      await handleStatusChange(selectedPatient._id, "readyToCall", note);
      setModalIsOpen(false);
      setNote("");
      fetchPatients();
    }
  };

  const openNotesDisplayModal = (patient) => {
    setSelectedPatient(patient);
    setShowNotesModal(true);
  };

  const closeNotesDisplayModal = () => {
    setShowNotesModal(false);
    setSelectedPatient(null);
  };

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
  };

  const openActionModal = (patient) => {
    setSelectedPatient(patient);
    setActionModalIsOpen(true);
  };

  const getCardClassName = (patient) => {
    if (
      filter === "needReady" &&
      patient.typeOfWork === "Encerado Diagnostico" &&
      patient.tipoDentes === "Foto para mostrar ao paciente" &&
      patient.enceradoCompleted
    ) {
      return "";
    }

    if (filter === "needReady") {
      if (
        patient.typeOfWork === "Encerado Diagnostico" &&
        patient.tipoDentes === "Foto para mostrar ao paciente" &&
        !patient.enceradoCompleted
      ) {
        return "encerado-foto-pending";
      }
      if (isDueSoon(patient.dueDate) && patient.laboratorio === "externo") {
        return "half-blue-half-red";
      }
      if (patient.laboratorio === "externo") {
        return "green-background";
      }
      if (isDueSoon(patient.dueDate)) {
        return "due-soon";
      }
    }

    if (filter === "readyToCall" && hasNotes(patient)) {
      return "has-notes";
    }

    return "";
  };

  const handleMarkAsCalled = async (patient) => {
    await handleStatusChange(patient._id, "called");
    setActionModalIsOpen(false);
    setSelectedPatient(null);
  };

  const handleMarkAsReadyToCall = async (patient) => {
    await handleStatusChange(patient._id, "readyToCall");
  };

  const handleMarkAsCompleted = async (patient) => {
    await handleStatusChange(patient._id, "completed");
  };

  const handleDeletePatient = async (id) => {
    try {
      await axios.delete(`https://backendlab.vercel.app/patients/${id}`);
      fetchPatients();
      setConfirmationModalIsOpen(false);
      setPatientToDelete(null);
    } catch (error) {
      console.error("Error deleting patient:", error);
    }
  };

  const handleDeletePhoto = async (patient, photo) => {
    try {
      const encodedId = encodeURIComponent(photo.publicId);
      const response = await axios.delete(
        `https://backendlab.vercel.app/patients/${patient._id}/photos/${encodedId}`
      );
      setSelectedPatient(response.data);
      fetchPatients();
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  const handleDownloadPhoto = async (photo) => {
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = photo.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading photo:", error);
    }
  };

  const handleUploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedPatient) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("photos", file));
      const response = await axios.post(
        `https://backendlab.vercel.app/patients/${selectedPatient._id}/photos`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSelectedPatient(response.data);
      fetchPatients();
    } catch (error) {
      console.error("Error uploading photo:", error);
    }
    setUploadingPhoto(false);
    e.target.value = "";
  };

  const openDeleteConfirmationModal = (patient) => {
    setPatientToDelete(patient);
    setConfirmationModalIsOpen(true);
  };

  const closeDeleteConfirmationModal = () => {
    setConfirmationModalIsOpen(false);
    setPatientToDelete(null);
  };

  const formatDate = (date, includeTime = false) => {
    if (!date) return "";
    const options = includeTime
      ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }
      : { day: "2-digit", month: "2-digit", year: "numeric" };
    return new Date(date).toLocaleDateString("en-GB", options);
  };

  const formatColocadoTime = (date) => {
    if (!date) return "N/A";
    return format(new Date(date), "dd/MM/yyyy HH:mm");
  };

  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const twoDaysLater = new Date();
    twoDaysLater.setDate(today.getDate() + 2);
    return new Date(dueDate) <= twoDaysLater;
  };

  const hasNotes = (patient) => patient.callNotes && patient.callNotes.trim() !== "";

  const getStatusText = (status) => {
    switch (status) {
      case "notYetFilledOut": return "Por Preencher";
      case "needReady": return "A executar";
      case "readyToCall": return "Precisa Chamada";
      case "called": return "Foram chamados";
      case "completed": return "Trabalhos Colocados";
      default: return status;
    }
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case "notYetFilledOut": return "red";
      case "needReady": return "blue";
      case "readyToCall": return "orange";
      case "called": return "green";
      case "completed": return "purple";
      default: return "gray";
    }
  };

  const filteredPatients = patients.filter((patient) => {
    if (filter !== "filterAll" && patient.status !== filter) return false;
    if (selectedDoctor !== "Todos" && patient.doctorName !== selectedDoctor) return false;
    return true;
  });

  const isFotoPending = (patient) =>
    patient.typeOfWork === "Encerado Diagnostico" &&
    patient.tipoDentes === "Foto para mostrar ao paciente" &&
    !patient.enceradoCompleted &&
    !allPatients.some(
      (p) => p.typeOfWork === "Chamar para mostrar!!!!" && p.name === patient.name
    );

  return (
    <>
      <TransitionGroup
        className="patient-list"
        onScroll={(e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.target;
          if (scrollHeight - scrollTop - clientHeight < 150) {
            if (typeof onLoadMore === "function") onLoadMore();
          }
        }}
      >
        {filteredPatients
          .sort((a, b) => {
            if (filter === "filterAll" || filter === "needReady") {
              return new Date(b.entryTime) - new Date(a.entryTime);
            }
            return new Date(b.updatedAt || b.entryTime) - new Date(a.updatedAt || a.entryTime);
          })
          .map((patient) => (
            <CSSTransition key={patient._id} timeout={300} classNames="patient">
              <div
                className={`patient-card ${getCardClassName(patient)}`}
                onClick={() => handlePatientClick(patient)}
              >
                {filter === "filterAll" && (
                  <div className="status-label" style={{ borderColor: getStatusBorderColor(patient.status) }}>
                    {getStatusText(patient.status)}
                  </div>
                )}

                <div className="category" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {filter === "filterAll" ? `${patient.name}` : patient.name}
                  {patient.photos && patient.photos.length > 0 && (
                    <span style={{
                      width: "7px", height: "7px", borderRadius: "50%",
                      backgroundColor: "#4f7a9a", display: "inline-block", flexShrink: 0,
                    }} title={`${patient.photos.length} foto(s)`} />
                  )}
                </div>

                {filter === "filterAll" && (
                  <>
                    <div className="category">Entrada: {formatDate(patient.entryTime, true)}</div>
                    <div className="category">Dr: {patient.doctorName}</div>
                  </>
                )}

                {filter === "needReady" && (
                  <>
                    <div className="category" style={{ paddingLeft: "50px" }}>Prazo: {formatDate(patient.dueDate)}</div>
                    <div className="category">Entrada: {formatDate(patient.entryTime, true)}</div>
                  </>
                )}

                {filter === "readyToCall" && (
                  <>
                    <div className="category">Dr: {patient.doctorName}</div>
                    <div className="category">Tipo: {patient.typeOfWork}</div>
                  </>
                )}

                {filter === "called" && (
                  <>
                    <div className="category">Dr: {patient.doctorName}</div>
                    <div className="category">Asst: {patient.assistantName}</div>
                    <div className="category">Tipo: {patient.typeOfWork}</div>
                  </>
                )}

                {filter === "completed" && (
                  <div className="category">Colocado em: {formatColocadoTime(patient.colocadoTime)}</div>
                )}

                <div className="buttons-container">
                  {filter === "needReady" && (
                    <>
                      {isFotoPending(patient) ? (
                        <button
                          className="button-33 button-completed"
                          onClick={(e) => { e.stopPropagation(); setEnceradoConfirmPatient(patient); }}
                        >
                          Aprovado?
                        </button>
                      ) : (
                        <button
                          className="button-33 button-mark-ready"
                          onClick={(e) => { e.stopPropagation(); handleMarkAsReadyToCall(patient); }}
                        >
                          Chamar (Pronto)
                        </button>
                      )}
                    </>
                  )}

                  {filter === "readyToCall" && (
                    <>
                      {patient.callNotes && patient.callNotes.trim() !== "" && (
                        <button
                          className="button-33 button-notes"
                          onClick={(e) => { e.stopPropagation(); openNotesDisplayModal(patient); }}
                        >
                          Notas
                        </button>
                      )}
                      <button
                        className="button-33 button-mark-called"
                        onClick={(e) => { e.stopPropagation(); openActionModal(patient); }}
                      >
                        Chamado?
                      </button>
                    </>
                  )}

                  {filter === "called" && (
                    <>
                      <button
                        className="button-33 button-completed"
                        onClick={(e) => { e.stopPropagation(); handleMarkAsCompleted(patient); }}
                      >
                        Completo
                      </button>
                      <button
                        className="button-33 button-revert-to-need-ready"
                        onClick={(e) => { e.stopPropagation(); handleRevertToNeedReady(patient._id); }}
                      >
                        A Executar
                      </button>
                    </>
                  )}

                  {filter === "filterAll" && (
                    <button
                      className="delete-button"
                      onClick={(e) => { e.stopPropagation(); openDeleteConfirmationModal(patient); }}
                    />
                  )}
                </div>
              </div>
            </CSSTransition>
          ))}
      </TransitionGroup>

      {selectedPatient &&
        !showNotesModal &&
        !actionModalIsOpen &&
        !modalIsOpen &&
        !confirmationModalIsOpen &&
        !photoToDelete && (
          <Modal
            isOpen={!!selectedPatient}
            onRequestClose={() => setSelectedPatient(null)}
            style={{
              content: {
                top: "50%", left: "50%", right: "auto", bottom: "auto",
                marginRight: "-50%", transform: "translate(-50%, -50%)",
                width: "560px", maxWidth: "92vw", padding: "24px",
                backgroundColor: "#ffffff", borderRadius: "14px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)", border: "none",
              },
            }}
          >
            <div className="patient-display-header">
              <div className="patient-display-name">{selectedPatient.name}</div>
              <div className="patient-display-sub">
                {getStatusText(selectedPatient.status)} • Dr: {selectedPatient.doctorName || "—"}
              </div>
            </div>

            <div className="patient-display-grid">
              <div className="patient-display-box">
                <div className="patient-display-label">Tipo de trabalho</div>
                <div className="patient-display-value">{selectedPatient.typeOfWork || "—"}</div>
              </div>
              <div className="patient-display-box">
                <div className="patient-display-label">Assistente</div>
                <div className="patient-display-value">{selectedPatient.assistantName || "—"}</div>
              </div>
              <div className="patient-display-box">
                <div className="patient-display-label">Laboratório</div>
                <div className="patient-display-value">{selectedPatient.laboratorio || "—"}</div>
              </div>
              <div className="patient-display-box">
                <div className="patient-display-label">Prazo de Entrega</div>
                <div className="patient-display-value">{formatDate(selectedPatient.dueDate) || "—"}</div>
              </div>
              <div className="patient-display-box">
                <div className="patient-display-label">Data de Entrada</div>
                <div className="patient-display-value">{formatDate(selectedPatient.entryTime, true) || "—"}</div>
              </div>
              <div className="patient-display-box">
                <div className="patient-display-label">Estado</div>
                <div className="patient-display-value">{getStatusText(selectedPatient.status)}</div>
              </div>

              {selectedPatient.typeOfWork === "coroa" && selectedPatient.material && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Material</div>
                  <div className="patient-display-value">{selectedPatient.material}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "coroa" && selectedPatient.cor && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Cor</div>
                  <div className="patient-display-value">{selectedPatient.cor}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "prótese fixa" && selectedPatient.material2 && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Material</div>
                  <div className="patient-display-value">{selectedPatient.material2}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "prótese fixa" && selectedPatient.cor2 && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Cor</div>
                  <div className="patient-display-value">{selectedPatient.cor2}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "Encerado Diagnostico" && selectedPatient.dentes && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Dentes</div>
                  <div className="patient-display-value">{selectedPatient.dentes}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "Encerado Diagnostico" && selectedPatient.tipoDentes && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Tipo</div>
                  <div className="patient-display-value">{selectedPatient.tipoDentes}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "Encerado Diagnostico" && selectedPatient.software && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Software</div>
                  <div className="patient-display-value">{selectedPatient.software}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "Encerado Diagnostico" &&
                selectedPatient.tipoDentes === "Foto para mostrar ao paciente" && (
                  <div className="patient-display-box">
                    <div className="patient-display-label">Aprovado?</div>
                    <div className="patient-display-value">
                      {selectedPatient.enceradoCompleted ? "Sim" : "Ainda Não"}
                    </div>
                  </div>
                )}
              {selectedPatient.laboratorio === "externo" && selectedPatient.externalDetails && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Laboratório Externo</div>
                  <div className="patient-display-value">{selectedPatient.externalDetails}</div>
                </div>
              )}
              {selectedPatient.typeOfWork === "goteiras" && selectedPatient.goteiraType && (
                <div className="patient-display-box">
                  <div className="patient-display-label">Tipo de Goteira</div>
                  <div className="patient-display-value">{selectedPatient.goteiraType}</div>
                </div>
              )}
            </div>

            <div className="patient-notes">
              <div className="patient-notes-title">Notas</div>
              <div className="patient-notes-text">
                {selectedPatient.notes?.trim() ? selectedPatient.notes : "Sem notas."}
              </div>
            </div>

            {selectedPatient.callNotes && selectedPatient.callNotes.trim() !== "" && (
              <div className="patient-notes patient-call-notes-box">
                <div className="patient-notes-title">Notas de chamada</div>
                <div className="patient-notes-text">{selectedPatient.callNotes}</div>
              </div>
            )}

            {selectedPatient.photos && selectedPatient.photos.length > 0 && (
              <div className="patient-photos-section">
                <div className="patient-notes-title">
                  Fotografias ({selectedPatient.photos.length})
                </div>
                {selectedPatient.photos.map((photo) => (
                  <div key={photo.publicId} className="photo-row photo-row-view">
                    <span className="photo-name">📎 {photo.name}</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <a
                        href={photo.url}
                        target="_blank"
                        rel="noreferrer"
                        className="photo-action-btn photo-download-btn"
                        title="Ver"
                        style={{ background: "#e8f4e8", color: "#1f6b45" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        👁
                      </a>
                      <button
                        className="photo-action-btn photo-download-btn"
                        title="Descarregar"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPhoto(photo);
                        }}
                      >
                        ↓
                      </button>
                      <button
                        className="photo-action-btn photo-delete-btn"
                        title="Eliminar"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhotoToDelete({ patient: selectedPatient, photo });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              ref={detailFileInputRef}
              multiple
              accept="image/*,.pdf"
              style={{ display: "none" }}
              onChange={handleUploadPhoto}
            />

            <div className="patient-display-actions">
              <button
                className="photo-add-btn"
                style={{ marginBottom: 0, alignSelf: "flex-start" }}
                type="button"
                onClick={() => detailFileInputRef.current.click()}
                disabled={uploadingPhoto}
              >
                {uploadingPhoto ? "A carregar..." : "+ Adicionar Fotografias"}
              </button>
              <button className="patient-close-button" onClick={() => setSelectedPatient(null)}>Fechar</button>
              <button
                className="patient-edit-button"
                onClick={() => {
                  const patientToEdit = selectedPatient;
                  setSelectedPatient(null);
                  openModal(patientToEdit);
                }}
              >
                Editar
              </button>
            </div>
          </Modal>
        )}

      {confirmationModalIsOpen && (
        <Modal isOpen={confirmationModalIsOpen} onRequestClose={closeDeleteConfirmationModal} style={customModalStyles}>
          <div className="confirmation-modal">
            <h2>Apagar paciente?</h2>
            <div className="confirmation-modal-buttons">
              <button className="confirm-button" onClick={() => handleDeletePatient(patientToDelete._id)}>Sim</button>
              <button className="cancel-button" onClick={closeDeleteConfirmationModal}>Cancelar</button>
            </div>
          </div>
        </Modal>
      )}

      {selectedPatient && showNotesModal && (
        <Modal isOpen={showNotesModal} onRequestClose={closeNotesDisplayModal} style={customModalStyles}>
          <h2>Notas para {selectedPatient.name}</h2>
          <p>{selectedPatient.callNotes || "Sem Notas."}</p>
          <button onClick={closeNotesDisplayModal}>Fechar</button>
        </Modal>
      )}

      {actionModalIsOpen && (
        <Modal isOpen={actionModalIsOpen} onRequestClose={() => setActionModalIsOpen(false)} style={customModalStyles}>
          <h2 className="modal-heading">{selectedPatient?.name}</h2>
          <div>
            <button className="modal-button" onClick={async () => { await handleMarkAsCalled(selectedPatient); setActionModalIsOpen(false); }}>Pronto</button>
            <button className="modal-button" onClick={() => { setActionModalIsOpen(false); setModalIsOpen(true); }}>Adicionar Notas</button>
          </div>
        </Modal>
      )}

      {selectedPatient && modalIsOpen && (
        <Modal isOpen={modalIsOpen} onRequestClose={() => { setModalIsOpen(false); setSelectedPatient(null); }} style={customModalStyles}>
          <h2 className="modal-heading">Notas para {selectedPatient.name}</h2>
          <textarea className="modal-textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notas aqui" />
          <button className="modal-button" onClick={async () => { await handleNoteSubmit(); fetchPatients(); }}>Salvar</button>
          <button className="modal-cancel-button" onClick={() => setModalIsOpen(false)}>Cancelar</button>
        </Modal>
      )}

      {enceradoConfirmPatient && (
        <Modal isOpen={!!enceradoConfirmPatient} onRequestClose={() => setEnceradoConfirmPatient(null)} style={customModalStyles}>
          <div className="confirmation-modal">
            <h2 className="modal-heading">Aprovado?</h2>
            <div className="confirmation-modal-buttons">
              <button className="confirm-button" onClick={async () => { await handleEnceradoComplete(enceradoConfirmPatient); setEnceradoConfirmPatient(null); }}>Sim</button>
              <button className="cancel-button" onClick={async () => { await handleDeletePatient(enceradoConfirmPatient._id); setEnceradoConfirmPatient(null); }}>Não</button>
            </div>
          </div>
        </Modal>
      )}

      {photoToDelete && (
        <Modal isOpen={!!photoToDelete} onRequestClose={() => setPhotoToDelete(null)} style={customModalStyles}>
          <div className="confirmation-modal">
            <h2>Apagar fotografia?</h2>
            <p style={{ fontSize: "13px", color: "#4f6070", marginBottom: "16px" }}>
              {photoToDelete.photo.name}
            </p>
            <div className="confirmation-modal-buttons">
              <button
                className="confirm-button"
                onClick={async () => {
                  await handleDeletePhoto(photoToDelete.patient, photoToDelete.photo);
                  setPhotoToDelete(null);
                }}
              >
                Sim
              </button>
              <button className="cancel-button" onClick={() => setPhotoToDelete(null)}>
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default PatientList;